import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Badge
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import moment from "moment";
import axios from "axios";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from "recharts";

const API_BASE = "https://maasradarapi.stechomeyazilim.info";

const COLORS = ["#556ee6", "#34c38f", "#f46a6a", "#f1b44c", "#50a5f1", "#74788d"];

const Dashboard = props => {
  document.title = "Anasayfa | Maaş Radar Admin Paneli";

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalJobPosts: 0,
    totalCompanies: 0,
    totalCommunityPosts: 0,
    totalSalaryEntries: 0,
    totalReports: 0,
    todayUsers: 0,
    todayJobPosts: 0,
    activeJobPosts: 0,
    latestUsers: [],
    latestJobPosts: [],
    latestComments: [],
    latestReports: [],
    userChartData: [],
    jobPostChartData: [],
    userTypeData: [],
    loading: true,
  });

  const loadStats = async () => {
    try {
      const today = moment().startOf("day").toISOString();
      const weekAgo = moment().subtract(6, "days").startOf("day").toISOString();

      const [
        usersRes, jobPostsRes, companiesRes, communityPostsRes,
        salaryEntriesRes, reportsRes, usersCountRes, jobPostsCountRes,
        usersWeekRes, jobPostsWeekRes, commentsRes
      ] = await Promise.allSettled([
        axios.get(`${API_BASE}/Users?$top=5&$orderby=CreatedAt desc&$select=Id,FullName,Email,Phone,CreatedAt,UserType`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/JobPosts?$top=5&$orderby=CreatedAt desc&$select=Id,PositionName,CompanyId,CreatedAt,IsDeleted,IsActive&$expand=Companies($select=Name)`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/Companies?$count=true&$top=0`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/CommunityPosts?$count=true&$top=0`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/SalaryEntries?$count=true&$top=0`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/ReportedComments?$count=true&$top=0`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/Users?$count=true&$top=0`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/JobPosts?$count=true&$top=0`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/Users?$filter=CreatedAt ge ${weekAgo}&$top=100&$orderby=CreatedAt desc&$select=Id,CreatedAt`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/JobPosts?$filter=CreatedAt ge ${weekAgo}&$top=100&$orderby=CreatedAt desc&$select=Id,CreatedAt`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/CommunityPostComments?$top=5&$orderby=CreatedAt desc&$select=Id,Content,CreatedAt`, { headers: getAuthHeaders() }),
      ]);

      const usersData = usersRes.status === "fulfilled" ? (usersRes.value.data.value || []) : [];
      const jobPostsData = jobPostsRes.status === "fulfilled" ? (jobPostsRes.value.data.value || []) : [];
      const commentsData = commentsRes.status === "fulfilled" ? (commentsRes.value.data.value || []) : [];
      const usersCount = usersCountRes.status === "fulfilled" ? (usersCountRes.value.data['@odata.count'] || 0) : 0;
      const jobPostsCount = jobPostsCountRes.status === "fulfilled" ? (jobPostsCountRes.value.data['@odata.count'] || 0) : 0;
      const companiesCount = companiesRes.status === "fulfilled" ? (companiesRes.value.data['@odata.count'] || 0) : 0;
      const communityPostsCount = communityPostsRes.status === "fulfilled" ? (communityPostsRes.value.data['@odata.count'] || 0) : 0;
      const salaryEntriesCount = salaryEntriesRes.status === "fulfilled" ? (salaryEntriesRes.value.data['@odata.count'] || 0) : 0;
      const reportsCount = reportsRes.status === "fulfilled" ? (reportsRes.value.data['@odata.count'] || 0) : 0;
      const usersWeekData = usersWeekRes.status === "fulfilled" ? (usersWeekRes.value.data.value || []) : [];
      const jobPostsWeekData = jobPostsWeekRes.status === "fulfilled" ? (jobPostsWeekRes.value.data.value || []) : [];

      // Today counts
      const todayUsers = usersData.filter(u => u.CreatedAt && moment(u.CreatedAt).isSameOrAfter(today)).length;
      const todayJobPosts = jobPostsData.filter(j => j.CreatedAt && moment(j.CreatedAt).isSameOrAfter(today)).length;
      const activeJobPosts = jobPostsData.filter(j => !j.IsDeleted).length;

      // User type distribution
      const userTypes = {};
      usersData.forEach(u => { userTypes[u.UserType || "Bilinmiyor"] = (userTypes[u.UserType || "Bilinmiyor"] || 0) + 1; });
      const userTypeData = Object.entries(userTypes).map(([name, value]) => ({ name, value }));

      // Last 7 days chart data
      const days = Array.from({ length: 7 }, (_, i) => moment().subtract(6 - i, "days").format("DD.MM"));
      const userChartData = days.map(day => ({
        name: day,
        Kullanıcı: usersWeekData.filter(u => moment(u.CreatedAt).format("DD.MM") === day).length
      }));
      const jobPostChartData = days.map(day => ({
        name: day,
        İlan: jobPostsWeekData.filter(j => moment(j.CreatedAt).format("DD.MM") === day).length
      }));

      setStats({
        totalUsers: usersCount,
        totalJobPosts: jobPostsCount,
        totalCompanies: companiesCount,
        totalCommunityPosts: communityPostsCount,
        totalSalaryEntries: salaryEntriesCount,
        totalReports: reportsCount,
        todayUsers,
        todayJobPosts,
        activeJobPosts,
        latestUsers: usersData,
        latestJobPosts: jobPostsData,
        latestComments: commentsData,
        latestReports: [],
        userChartData,
        jobPostChartData,
        userTypeData,
        loading: false,
      });
    } catch (error) {
      console.error("Dashboard verileri alınamadı:", error);
      setStats((prev) => ({ ...prev, loading: false }));
    }
  };

  function getAuthHeaders() {
    const token = localStorage.getItem("token");
    return {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    };
  }

  useEffect(() => { loadStats(); }, []);

  const statCards = [
    { title: "Toplam Kullanıcı", value: stats.totalUsers, icon: "bx-user", color: "bg-primary" },
    { title: "Toplam İş İlanı", value: stats.totalJobPosts, icon: "bx-briefcase", color: "bg-success" },
    { title: "Şirketler", value: stats.totalCompanies, icon: "bx-buildings", color: "bg-info" },
    { title: "Topluluk Gönderileri", value: stats.totalCommunityPosts, icon: "bx-message-square-detail", color: "bg-warning" },
    { title: "Maaş Girişleri", value: stats.totalSalaryEntries, icon: "bx-money", color: "bg-secondary" },
    { title: "Bekleyen Rapor", value: stats.totalReports, icon: "bx-error-circle", color: "bg-danger" },
    { title: "Bugünkü Kayıt", value: stats.todayUsers, icon: "bx-user-plus", color: "bg-primary" },
    { title: "Bugünkü İlan", value: stats.todayJobPosts, icon: "bx-briefcase-alt", color: "bg-success" },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Anasayfa" breadcrumbItem="Dashboard" />
          {stats.loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-2 text-muted">Yükleniyor...</p>
            </div>
          ) : (
            <>
              <Row>
                {statCards.map((card, idx) => (
                  <Col xl="3" md="6" key={idx}>
                    <Card className="mini-stat">
                      <CardBody>
                        <div className="d-flex">
                          <div className="flex-grow-1">
                            <p className="text-muted fw-medium mb-2">{card.title}</p>
                            <h4 className="mb-0">{card.value}</h4>
                          </div>
                          <div className={`avatar-sm rounded-circle ${card.color} align-self-center`}>
                            <span className="avatar-title">
                              <i className={`bx ${card.icon} font-size-24 text-white`}></i>
                            </span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>

              <Row>
                <Col xl={8}>
                  <Card>
                    <CardBody>
                      <h4 className="card-title mb-4">Son 7 Gün Kullanıcı Kaydı</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={stats.userChartData}>
                          <defs>
                            <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#556ee6" stopOpacity={0.8}/>
                              <stop offset="95%" stopColor="#556ee6" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis allowDecimals={false} />
                          <Tooltip />
                          <Area type="monotone" dataKey="Kullanıcı" stroke="#556ee6" fillOpacity={1} fill="url(#colorUsers)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardBody>
                  </Card>
                </Col>
                <Col xl={4}>
                  <Card>
                    <CardBody>
                      <h4 className="card-title mb-4">Kullanıcı Tipi Dağılımı</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie data={stats.userTypeData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} fill="#8884d8" paddingAngle={5} dataKey="value" label>
                            {stats.userTypeData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col xl={6}>
                  <Card>
                    <CardBody>
                      <h4 className="card-title mb-4">Son 7 Gün İş İlanı Trendi</h4>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={stats.jobPostChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis allowDecimals={false} />
                          <Tooltip />
                          <Bar dataKey="İlan" fill="#34c38f" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardBody>
                  </Card>
                </Col>
                <Col xl={6}>
                  <Card>
                    <CardBody>
                      <h4 className="card-title mb-4">Son Aktiviteler</h4>
                      <div className="table-responsive">
                        <table className="table table-centered table-nowrap mb-0">
                          <tbody>
                            {stats.latestUsers.slice(0, 3).map((u, i) => (
                              <tr key={`u${i}`}>
                                <td style={{ width: 50 }}><div className="avatar-xs"><span className="avatar-title rounded-circle bg-primary text-white"><i className="bx bx-user"></i></span></div></td>
                                <td><h5 className="font-size-14 mb-1">{u.FullName || "Yeni Kullanıcı"}</h5><p className="text-muted mb-0">Kayıt oldu</p></td>
                                <td className="text-end">{u.CreatedAt ? moment(u.CreatedAt).fromNow() : "-"}</td>
                              </tr>
                            ))}
                            {stats.latestJobPosts.slice(0, 2).map((j, i) => (
                              <tr key={`j${i}`}>
                                <td style={{ width: 50 }}><div className="avatar-xs"><span className="avatar-title rounded-circle bg-success text-white"><i className="bx bx-briefcase"></i></span></div></td>
                                <td><h5 className="font-size-14 mb-1">{j.Title || "Yeni İlan"}</h5><p className="text-muted mb-0">{j.CompanyName || "-"}</p></td>
                                <td className="text-end">{j.CreatedAt ? moment(j.CreatedAt).fromNow() : "-"}</td>
                              </tr>
                            ))}
                            {stats.latestComments.slice(0, 2).map((c, i) => (
                              <tr key={`c${i}`}>
                                <td style={{ width: 50 }}><div className="avatar-xs"><span className="avatar-title rounded-circle bg-info text-white"><i className="bx bx-comment"></i></span></div></td>
                                <td><h5 className="font-size-14 mb-1">Yeni Yorum</h5><p className="text-muted mb-0">{c.Content ? c.Content.substring(0, 40) + "..." : "-"}</p></td>
                                <td className="text-end">{c.CreatedAt ? moment(c.CreatedAt).fromNow() : "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col xl="6">
                  <Card>
                    <CardBody>
                      <h4 className="card-title mb-4">Son Kayıt Olan Kullanıcılar</h4>
                      <div className="table-responsive">
                        <table className="table table-hover table-centered table-nowrap mb-0">
                          <thead>
                            <tr><th>Ad Soyad</th><th>E-posta / Telefon</th><th>Tip</th><th>Tarih</th></tr>
                          </thead>
                          <tbody>
                            {stats.latestUsers.length === 0 && (
                              <tr><td colSpan="4" className="text-center text-muted">Kayıt bulunamadı</td></tr>
                            )}
                            {stats.latestUsers.map((user, i) => (
                              <tr key={i}>
                                <td>{user.FullName || "-"}</td>
                                <td>{user.Email || user.Phone || "-"}</td>
                                <td><Badge color={user.UserType === "Admin" ? "danger" : user.UserType === "Isveren" ? "success" : "info"}>{user.UserType || "-"}</Badge></td>
                                <td>{user.CreatedAt ? moment(user.CreatedAt).format("DD.MM.YYYY HH:mm") : "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col xl="6">
                  <Card>
                    <CardBody>
                      <h4 className="card-title mb-4">Son İş İlanları</h4>
                      <div className="table-responsive">
                        <table className="table table-hover table-centered table-nowrap mb-0">
                          <thead>
                            <tr><th>Başlık</th><th>Şirket</th><th>Durum</th><th>Tarih</th></tr>
                          </thead>
                          <tbody>
                            {stats.latestJobPosts.length === 0 && (
                              <tr><td colSpan="4" className="text-center text-muted">Kayıt bulunamadı</td></tr>
                            )}
                            {stats.latestJobPosts.map((post, i) => (
                              <tr key={i}>
                                <td>{post.PositionName || "-"}</td>
                                <td>{post.Companies?.Name || "-"}</td>
                                <td>
                                  {post.IsDeleted ? <Badge color="danger">Silinmiş</Badge> :
                                   post.IsActive === false ? <Badge color="warning">Pasif</Badge> :
                                   <Badge color="success">Aktif</Badge>}
                                </td>
                                <td>{post.CreatedAt ? moment(post.CreatedAt).format("DD.MM.YYYY HH:mm") : "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

Dashboard.propTypes = {
  t: PropTypes.any
};

export default withTranslation()(Dashboard);
